<template>
    <div class="modal-full">
        <div class="modal-header">
            <close-icon v-on:click="closeView"></close-icon>
            <h1>Manage Container</h1>
        </div>

        <div class="modal-body no-pad" v-if="container">
            <div class="flex h-full">
                <div class="account-sidebar h-full">
                    <div class="mb-4 text-center">
                        <span class="fa-layers fa-4x mb-3">
                            <i class="fa fa-circle"></i>
                            <i class="fas fa-user fa-inverse" data-fa-transform="shrink-6.5"></i>
                        </span>

                        <p class="text-lg font-light">{{ container.name }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Container ID</label>
                        <p>{{ container.id }}</p>

                        <label>Domain</label>
                        <p>{{ container.domain }}</p>
                    </div>
                </div>

                <div class="account-body">
                    <form class="profile-form">
                        <div class="form-title">
                            <p class="text-lg font-light">Container</p>
                        </div>

                        <div class="form-field">
                            <input type="text" v-model="container.name">
                            <label>Container Name</label>
                        </div>

                        <div class="form-field">
                            <input type="number" v-model="container.flags.max_accounts">
                            <label>Maximum number of accounts</label>
                        </div>

                        <div class="form-field" v-if="packages">
                            <select v-model="container.flags.default_package">
                                <option :value="null">No Package Selected</option>

                                <option v-for="packageItem in packages" :value="packageItem.id">
                                    {{ packageItem.label }}
                                </option>
                            </select>
                            <label>Container Package</label>
                        </div>

                        <div class="form-field">
                            <input type="text" v-model="container.domain">
                            <label>Domain</label>
                        </div>

                        <div class="form-field">
                            <select v-model="container.secure">
                                <option value="0">No</option>
                                <option value="1">Yes</option>
                            </select>
                            <label>Secure (Uses SSL)</label>
                        </div>

                        <div class="form-field">
                            <select v-model="container.public">
                                <option :value="false">No</option>
                                <option :value="true">Yes</option>
                            </select>
                            <label>Public Site Enabled?</label>
                        </div>

                        <div class="form-title">
                            <p class="text-lg font-light">Design</p>
                        </div>

                        <div class="form-field">
                            <input
                                type="text"
                                :value="getBrandingValue('application_template')"
                                target="application_template"
                                @input="setBrandingValue"
                            >
                            <label>Application Template</label>
                        </div>

                        <div class="form-field">
                            <input
                                type="text"
                                :value="getBrandingValue('view_directory')"
                                target="view_directory"
                                @input="setBrandingValue"
                            >
                            <label>Template Directory</label>
                        </div>

                        <div class="form-field">
                            <code-textarea
                                rows="6"
                                :value="getBrandingValue('custom_css')"
                                target="custom_css"
                                @input="setBrandingValue"
                            ></code-textarea>
                            <label>Custom CSS</label>
                        </div>

                        <div class="form-field">
                            <label>Logo Image</label>
                            <image-drop
                                class="w-full sm:w-1/2 md:w-1/4"
                                name="custom_logo"
                                :upload-url="logoUploadUrl"
                                :value="getBrandingValue('custom_logo')"
                                target="custom_logo"
                                @input="setBrandingValue"
                            ></image-drop>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <div class="modal-footer">
            <div class="flex">
                <div class="left text-left">
                    <div class="actions" v-click-away="hideActions">
                        <div class="actions-menu" v-show="actionsShown">
                            <button class="btn-primary muted" type="button" @click="handleFeatureFlags"
                                    v-if="container">Feature Flags
                            </button>
                            <button class="btn-primary muted" type="button" @click="handleAccountFlags"
                                    v-if="container">Default Account Flags
                            </button>
                            <button class="btn-danger muted" type="button" @click="toggleSuspensionStatus"
                                    v-if="container">{{ isContainerSuspended }}
                            </button>
                            <button class="btn-danger" type="button" @click="handleDelete" v-if="container">Delete
                            </button>
                        </div>

                        <button class="btn-primary muted" type="button" :data-open="actionsShown"
                                @click="actionsShown = !actionsShown">
                            <ChevronUpIcon class="h-6 w-6 inline" />
                            Actions
                        </button>
                    </div>
                </div>

                <div class="right text-right">
                    <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>
                    <button class="btn-primary" type="button" @click="saveForm">Save</button>
                </div>
            </div>
        </div>
        <router-view v-slot="{ Component }">
            <transition name="drop-in" mode="in-out">
                <component :is="Component"></component>
            </transition>
        </router-view>
    </div>
</template>

<script>
import CodeTextarea from '../../CodeTextarea'
import {ChevronUpIcon} from "@heroicons/vue/24/solid"

export default {
    name: 'ContainerView',
    data() {
        return {
            container: null,
            packages: null,
            defaultAccountFlags: {},
            actionsShown: false,
        }
    },
    components: {
        CodeTextarea,
        ChevronUpIcon,
    },
    computed: {
        logoUploadUrl() {
            return route('containers.logo', this.container.id)
        },
        isContainerSuspended: function () {
            return this.container.suspended ? 'Lift Suspension' : 'Suspend'
        }
    },
    mounted() {
        this.loadData()

        this.setMetaTitle('View Container | JournoLink Control')
    },
    beforeDestroy() {
        //
    },
    beforeRouteUpdate(to, from, next) {
        this.$route.params.containerId = to.params.containerId
        this.loadData()
        next()
    },
    methods: {
        getPackages() {
            return axios.get(route('packages.select'))
        },
        getContainer() {
            return axios.get(route('containers.show', this.$route.params.containerId))
        },
        loadData() {
            axios.all([this.getPackages(), this.getContainer()])
                .then(axios.spread((packagesResponse, containerResponse) => {
                    this.container = this.containerWithFlags(containerResponse.data.container)
                    this.packages = packagesResponse.data.results
                    this.initialiseAccountFlags(
                        (typeof this.container.flags.default_account_flags === 'string') ? JSON.parse(this.container.flags.default_account_flags) : {},
                        containerResponse.data.accountFlags
                    )
                    this.setMetaTitle('Manage Container | JournoLink Control')
                }))
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Hide the actions box
         */
        hideActions() {
            this.actionsShown = false
        },
        /**
         * Emit the close event to the parent
         *
         * @return {void}
         */
        closeView() {
            this.setMetaTitle('List Containers | JournoLink Control')
            this.$root.goBack()
        },
        /**
         *
         */
        saveForm() {
            axios.put(route('containers.update', this.$route.params.containerId), this.container)
                .then(response => {
                    this.container = this.containerWithFlags(response.data.container)
                    this.setMetaTitle('Manage Container | JournoLink Control')
                    this.$bus.$emit('alert.success', 'The Container details have been saved')
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Handle a suspend request event
         *
         * @return {void}
         */
        toggleSuspensionStatus() {
            let text = this.container.suspended ? 'This will remove suspension on the container' : 'This will suspend the container'
            this.alert.fire({
                title: 'Are you sure?',
                text: text,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#E3342F',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('containers.suspend', this.$route.params.containerId), {}).then(response => {
                            this.$bus.$emit('alert.success', 'Suspension update success')
                            this.container.suspended = !this.container.suspended
                        }).catch(e => {
                            this.$bus.$emit('alert.danger', 'Suspension update failed')
                        })
                    }
                })
        },
        /**
         * Handle a button-press to delete the Container
         *
         * @return {Object}
         */
        handleDelete() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'Are you sure you wish to delete this Container? This action will remove all Accounts, Businesses and Users associated with this Container',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#E3342F',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.delete(route('containers.delete', this.container.id))
                            .then(response => {
                                this.$bus.$emit('alert.success', 'Container and contents have been deleted')
                                this.$router.push({name: 'containers.list'})
                            })
                            .catch((e) => {
                                console.dir(e)
                            })
                    }
                })
        },
        handleAccountFlags() {
            this.$router.push({
                name: 'containers.show.account_flags', params: {
                    containerId: this.container.id,
                    flags: this.defaultAccountFlags
                }
            })
        },
        /**
         * Open the feature flags manager screen.
         */
        handleFeatureFlags() {
            this.$router.push({
                name: 'containers.features', params: {
                    containerId: this.container.id,
                }
            })
        },
        /**
         * Retrieve a branding value from the data
         *
         * @param  {String} key
         * @return {Mixed}
         */
        getBrandingValue(key) {
            return this.container.branding[key]
        },
        /**
         * Set a branding value into data
         *
         * @param {Object} event
         */
        setBrandingValue(event) {
            if (event.name) {
                return this.setBrandingValueByObject(event)
            }

            var key = event.target.getAttribute('target')

            if (this.container.branding.length === 0) {
                this.container.branding = {}
            }

            this.container.branding[key] = event.target.value
        },
        /**
         * Set a branding value into data when given an object
         *
         * @param {Object} object
         */
        setBrandingValueByObject(object) {
            var key = object.name

            if (this.container.branding.length === 0) {
                this.container.branding = {}
            }

            this.container.branding[key] = object.value
        },
        /**
         * Handle an [Escape] keypress
         *
         * @return {void}
         */
        onEscapeKeypress() {
            this.closeView()
        },
        /**
         * Initialise all account flags
         */
        initialiseAccountFlags(savedFlags, allAccountFlags) {
            for (var flag in allAccountFlags) {
                this.defaultAccountFlags[allAccountFlags[flag]] = savedFlags[allAccountFlags[flag]]
            }
        },
        /**
         * overwrite flags when empty array
         * @param  {object} container
         * @return {object} container
         */
        containerWithFlags(container) {
            if (Array.isArray(container.flags)) {
                container.flags = {}
            }
            return container
        }
    },
}
</script>

<style lang="less">
.account-sidebar {
    padding: 15px;
    width: 350px;
    font-size: 0.9rem;
    background: #f6f6f6;

    label {
        font-weight: bold;
        font-size: 0.8em;
        color: #999999;
    }

    p {
        margin-bottom: 0.5em;
    }
}

.account-activity {
    width: 250px;
    font-size: 0.9rem;
    padding: 15px;
}

.hidden-input {
    input {
        display: none;
        width: 100%;
    }

    .show {
        display: inline-block;
    }

    .hide {
        display: none;
    }

    .edit-field-link {
        text-decoration-line: underline;
        text-decoration-style: solid;
        color: #222222;
        word-wrap: break-word;
    }
}

.profile-form {
    .section-title {
        margin-top: 20px;
    }

    .form-title {
        margin-bottom: 20px;
    }
}
</style>
